import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';

import { Button, Text, Modal, Banner, Checkbox } from '@shopify/polaris';

import BatchGrid from '../../partials/BatchGrid';
import LoadingSpinner from '../../partials/LoadingSpinner';
import { useGetBatchQuery, useStopReturningMutation, useMarkReturnedMutation, useParkReturningMutation } from '../../../warehouse/store/batches';
import { sortItemsByBpn } from '../../../warehouse/utils/bpn';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { buttonProps, useDocumentTitle } from '../../../warehouse/utils/a11y';
import { UI_COLORS } from '../../../constants';

// Totes holding items to return get the highlight colour; other in-use
// totes stay muted, and the clicked tote filter uses the accent colour
const RETURN_TOTE_COLOR = UI_COLORS.accent;
const SELECTED_TOTE_COLOR = UI_COLORS.header;

const ReturnBatch = () => {
  useDocumentTitle('Return batch');
  const { batchId } = useParams();
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the stock returns permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('stock_return');
  const barcodeInputRef = useRef(null);

  // Load the claimed batch from the backend
  const { currentData: fetchedBatch, isLoading: isLoadingBatch, isFetching: isFetchingBatch, error: batchError, isError: isBatchError } = useGetBatchQuery(batchId, { skip: !batchId });
  const selectedBatch = batchId ? fetchedBatch ?? null : null;

  // Releases the batch back to 'to_return' when the returner stops
  const [stopReturning, { isLoading: isStopping }] = useStopReturningMutation();
  const [stopError, setStopError] = useState('');

  // Marks the batch 'complete' and stores the return results server-side
  const [markReturned, { isLoading: isMarkingReturned }] = useMarkReturnedMutation();
  const [completeError, setCompleteError] = useState('');

  // Parks the batch mid-return - progress is saved and the claim released
  // so any user can resume it
  const [parkReturning, { isLoading: isParking }] = useParkReturningMutation();
  const [parkError, setParkError] = useState('');

  // Items to return: the picked leftovers stored per tote in returnItems
  const returnItems = selectedBatch ? (selectedBatch.returnItems ?? []).flatMap(entry => entry.items ?? []) : [];

  // Totes that hold at least one return item - highlighted everywhere the
  // cart grid is shown so the returner knows which positions matter
  const returnToteHighlights = (selectedBatch?.returnItems ?? []).map(entry => ({
    row: entry.row,
    column: entry.column,
    color: RETURN_TOTE_COLOR,
  }));

  // State to track returned items
  const [returnedItems, setReturnedItems] = useState(new Set());

  // State for barcode scanner input
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isBarcodeFocused, setIsBarcodeFocused] = useState(false);

  // State for popup
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  // Queue for multiple items with same SKU
  const [itemQueue, setItemQueue] = useState([]);
  // Total items queued for the current SKU (itemQueue drains as they're confirmed)
  const [skuToteTotal, setSkuToteTotal] = useState(0);

  // State for tote filter (clicked tote in the grid)
  const [selectedTote, setSelectedTote] = useState(null);

  // State for BPN sort - the separator is the store setting that arrives
  // on the batch response, '-' until it (or a missing field) loads
  const [sortDirection, setSortDirection] = useState(null);
  const bpnSeparator = selectedBatch?.bpnSeparator ?? '-';

  // State for BPN filter (set when scanning a non-SKU value)
  const [bpnFilter, setBpnFilter] = useState(null);

  // State for hiding returned items from the list
  const [hideReturned, setHideReturned] = useState(true);

  // Filter/sort controls are collapsed by default - click the toggle row
  // to expand them
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Blocks every navigation away while a batch is loaded - the blocker
  // replaces the RN app's tabPress + hardwareBackPress + beforeRemove
  // interception. The ref is read at navigation time so performLeave can
  // clear it synchronously before navigating itself - without it that
  // navigate() would be intercepted by this same blocker.
  const allowLeaveRef = useRef(false);
  const blocker = useBlocker(() => !!selectedBatch && !allowLeaveRef.current);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setStopError('');
      setPopupType('leave-confirm');
      setPopupVisible(true);
    }
  }, [blocker.state]);

  // Resuming a parked batch hydrates the return state saved in
  // returning_state so the new returner continues where the last one left
  // off. Runs once per batch - later edits are the resuming user's own.
  const hydratedBatchId = useRef(null);
  useEffect(() => {
    const state = fetchedBatch?.returningState;
    if (!state || !fetchedBatch || hydratedBatchId.current === fetchedBatch.id) return;
    hydratedBatchId.current = fetchedBatch.id;
    setReturnedItems(new Set(state.returned_item_ids || []));
  }, [fetchedBatch]);

  // Manual click on an item asks for confirmation before marking it
  // returned. Clicking an already-returned item unmarks it directly.
  const handleItemPress = (item) => {
    if (returnedItems.has(item.id)) {
      setReturnedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    } else {
      setCurrentItem(item);
      setPopupType('manual-return');
      setPopupVisible(true);
    }
  };

  const handleConfirmManualReturn = () => {
    if (currentItem) {
      setReturnedItems(prev => new Set([...prev, currentItem.id]));
    }
    setPopupVisible(false);
    setPopupType(null);
    setCurrentItem(null);
    setItemQueue([]);
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  const handleBarcodeScan = (scannedBarcode) => {
    if (!selectedBatch || !scannedBarcode.trim()) return;

    // Find all items by SKU or UPC (can be multiple items in different totes)
    const scanValue = scannedBarcode.trim();
    const matchingItems = returnItems.filter(
      i => i.sku === scanValue || i.upc === scanValue
    );

    if (matchingItems.length === 0) {
      // No matching SKU - use the scanned value as a BPN prefix filter.
      // "ataf-" / "atab-" prefixes also set the sort direction.
      let filterValue = scannedBarcode.trim();
      let direction = null;
      if (filterValue.startsWith('ataf-')) {
        filterValue = filterValue.slice('ataf-'.length);
        direction = 'asc';
      } else if (filterValue.startsWith('atab-')) {
        filterValue = filterValue.slice('atab-'.length);
        direction = 'desc';
      }
      const matchedItems = returnItems.filter(i => (i.bpn || '').startsWith(filterValue));
      if (matchedItems.length === 0) {
        // Nothing matches - show guidance popup, don't apply the filter
        setCurrentItem(null);
        setPopupType('no-match');
        setPopupVisible(true);
      } else {
        setBpnFilter(filterValue);
        // BPN filter replaces the tote filter
        setSelectedTote(null);
        if (direction) setSortDirection(direction);
      }
    } else {
      // Filter out already-returned items
      const unreturnedItems = matchingItems.filter(item => !returnedItems.has(item.id));

      if (unreturnedItems.length === 0) {
        // All items already returned - show confirmation popup for first one
        setCurrentItem(matchingItems[0]);
        setPopupType('already-returned');
        setPopupVisible(true);
      } else {
        // Add unreturned items to queue
        setItemQueue(unreturnedItems);
        setSkuToteTotal(unreturnedItems.length);
        // Show popup for first item in queue
        setCurrentItem(unreturnedItems[0]);
        setPopupType('new-item');
        setPopupVisible(true);
      }
    }

    // Clear the input after scanning
    setBarcodeInput('');
  };

  const processNextInQueue = () => {
    const nextQueue = itemQueue
      .slice(1)
      .filter(i => !returnedItems.has(i.id));
    if (nextQueue.length > 0) {
      // Show next item in queue (queue includes the current item)
      setCurrentItem(nextQueue[0]);
      setItemQueue(nextQueue);
      setPopupType('new-item');
      setPopupVisible(true);
    } else {
      // No more items, clear queue and focus input
      setCurrentItem(null);
      setPopupType(null);
      setPopupVisible(false);
      setItemQueue([]);
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  };

  const handlePopupDismiss = () => {
    // For new item popups, default to marking as returned when dismissed
    if (popupType === 'new-item' && currentItem) {
      setReturnedItems(prev => new Set([...prev, currentItem.id]));
      processNextInQueue();
    } else {
      // For other popup types, just dismiss
      setPopupVisible(false);
      setPopupType(null);
      setCurrentItem(null);
      setItemQueue([]);
      // Dismissing the leave-confirm cancels the blocked navigation
      if (blocker.state === 'blocked') {
        blocker.reset();
      }
      // Focus the input field after popup is dismissed
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  };

  const handleMarkAsReturned = () => {
    if (currentItem) {
      setReturnedItems(prev => new Set([...prev, currentItem.id]));
      processNextInQueue();
    }
  };

  const handleConfirmReturn = () => {
    // For already returned items, just confirm and dismiss
    setPopupVisible(false);
    setPopupType(null);
    setCurrentItem(null);
    setItemQueue([]);
    // Focus the input field after popup is dismissed
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  // Resolved = returned. Progress counts unique return items only.
  const returnedCount = selectedBatch
    ? returnItems.filter(i => returnedItems.has(i.id)).length
    : 0;
  const allReturned = selectedBatch
    ? returnItems.length > 0 && returnedCount >= returnItems.length
    : false;

  // Auto-focus the barcode input after 1s of inactivity so it's always
  // ready for the next scan (unless a popup is open or the return is
  // done)
  useEffect(() => {
    if (!selectedBatch) return;
    if (isBarcodeFocused || popupVisible) return;
    if (allReturned) return;
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedBatch, isBarcodeFocused, popupVisible, returnedItems, allReturned]);

  // When the batch is fully returned, unfocus the barcode input
  useEffect(() => {
    if (allReturned) barcodeInputRef.current?.blur();
  }, [allReturned]);

  // Resumes the blocked navigation after a successful stop/park/complete
  // - or returns to the returns queue when no navigation was pending
  const performLeave = () => {
    setPopupVisible(false);
    setPopupType(null);
    setCurrentItem(null);
    setItemQueue([]);
    // Clear known returned items before leaving
    setReturnedItems(new Set());
    allowLeaveRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.proceed();
    } else {
      navigate('/warehouse/app/returns');
    }
  };

  // 'Stop Returning' tells the backend to return the batch to 'to_return'
  // and release this returner's claim before navigating away. If the
  // release fails the popup stays open with an error so they can retry.
  const handleStopReturning = async () => {
    if (!selectedBatch || isStopping) return;

    const result = await stopReturning(selectedBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setStopError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being returned by someone else.'
            : status === 409
              ? 'This batch is no longer being returned.'
              : 'Could not release the batch. Check your connection and try again.'
      );
      return;
    }

    setStopError('');
    performLeave();
  };

  // Confirming the complete-return popup marks the batch 'complete' on
  // the backend, along with which items were verified back in stock.
  const handleCompleteReturnConfirmed = async () => {
    if (!selectedBatch || isMarkingReturned) return;

    const result = await markReturned({
      batchId: selectedBatch.id,
      returnedItemIds: Array.from(returnedItems),
    });

    if ('error' in result) {
      const status = result.error?.status;
      setCompleteError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being returned by someone else.'
            : status === 409
              ? 'This batch is no longer being returned.'
              : status === 422
                ? 'Every item must be marked returned before the batch can be completed.'
                : 'Could not mark the batch returned. Check your connection and try again.'
      );
      return;
    }

    setCompleteError('');
    performLeave();
  };

  // 'Park Batch' asks for confirmation, then saves the current return
  // progress and releases the claim so any user can resume the batch.
  // Called from the header or the leave guard - a blocked navigation is
  // resumed after parking so it still takes the user where they headed
  const handleParkBatch = () => {
    setParkError('');
    setPopupType('park-return');
    setPopupVisible(true);
  };

  const handleParkReturnConfirmed = async () => {
    if (!selectedBatch || isParking) return;

    const result = await parkReturning({
      batchId: selectedBatch.id,
      returnedItemIds: Array.from(returnedItems),
    });

    if ('error' in result) {
      const status = result.error?.status;
      setParkError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being returned by someone else.'
            : status === 409
              ? 'This batch is no longer being returned.'
              : 'Could not park the batch. Check your connection and try again.'
      );
      return;
    }

    setParkError('');
    performLeave();
  };

  // Confirming completion navigates back to the batch list
  const handleMarkBatchReturned = () => {
    setCompleteError('');
    setPopupType('complete-return');
    setPopupVisible(true);
  };

  const handleToteFilterPress = (tote) => {
    setSelectedTote(prev =>
      prev && prev.row === tote.row && prev.column === tote.column ? null : { row: tote.row, column: tote.column }
    );
  };

  // Progress counts unique returned items
  const progress = returnedCount;
  const totalItems = returnItems.length;

  const filteredItems = selectedBatch
    ? returnItems.filter(
        i =>
          (!selectedTote || (i.row === selectedTote.row && i.column === selectedTote.column)) &&
          (!bpnFilter || (i.bpn || '').startsWith(bpnFilter)) &&
          (!hideReturned || !returnedItems.has(i.id))
      )
    : [];

  const visibleItems = sortDirection
    ? sortItemsByBpn(filteredItems, bpnSeparator, sortDirection)
    : filteredItems;

  const renderToteGrid = (row, column, toteSize = 16) => (
    <BatchGrid
      totalRows={selectedBatch?.totalRows ?? 0}
      totalColumns={selectedBatch?.totalColumns ?? 0}
      toteSize={toteSize}
      gap={3}
      highlightedTotes={[{ row, column, color: RETURN_TOTE_COLOR }]}
    />
  );

  const renderItem = (item) => {
    const isReturned = returnedItems.has(item.id);

    return (
      <div
        key={item.id}
        className={`warehouse-item-card${isReturned ? ' warehouse-item-picked' : ''}`}
        {...buttonProps(() => handleItemPress(item))}
      >
        <div className="warehouse-item-header">
          <span className="warehouse-item-name">{item.name}</span>
          <span className={`warehouse-item-badge${isReturned ? ' warehouse-item-badge-active' : ''}`}>
            {isReturned ? <><span aria-hidden="true">✓</span><span className="visually-hidden">Returned</span></> : ''}
          </span>
        </div>

        {item.sku && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">SKU:</span>
            <span>{item.sku}</span>
          </div>
        )}

        {item.upc && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">UPC:</span>
            <span>{item.upc}</span>
          </div>
        )}

        {item.variantOptions && item.variantOptions.length > 0 && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">Variant:</span>
            <span>{item.variantOptions.map(o => `${o.option_display_name}: ${o.label}`).join(', ')}</span>
          </div>
        )}

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">BPN:</span>
          <span>{item.bpn}</span>
        </div>

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">Qty:</span>
          <span>{item.quantity}</span>
        </div>

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">Shipment:</span>
          <span>{item.orderShipmentId}</span>
        </div>

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">Tote:</span>
          {renderToteGrid(item.row, item.column)}
        </div>
      </div>
    );
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header">
        <div className="warehouse-header-row">
          <div>
            <h2>Return Batch</h2>
            <div className="warehouse-header-subtitle">
              {selectedBatch ? selectedBatch.name : 'Active returns'}
            </div>
            {selectedBatch?.cartId ? (
              <div className="warehouse-header-cart">Cart: {selectedBatch.cartId}</div>
            ) : null}
          </div>
          {selectedBatch && (
            <Button onClick={handleParkBatch}>Park Batch</Button>
          )}
        </div>
      </div>

      {selectedBatch ? (
        <>
          <div className="warehouse-progress">
            <div
              className="warehouse-progress-bar"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={totalItems}
              aria-label="Return progress"
            >
              <div
                className="warehouse-progress-fill"
                style={{ width: `${totalItems > 0 ? (progress / totalItems) * 100 : 0}%` }}
              />
            </div>
            <div className="warehouse-progress-text">
              {progress} / {totalItems} items
            </div>
          </div>

          {!allReturned && (
            <div className="warehouse-scanner">
              <div className={`warehouse-scan-indicator${isBarcodeFocused ? ' warehouse-scan-indicator-ready' : ''}`} role="status">
                {isBarcodeFocused ? 'Ready to scan' : 'Not ready to scan'}
              </div>
              <input
                ref={barcodeInputRef}
                className={`warehouse-barcode-input${isBarcodeFocused ? ' warehouse-barcode-input-focused' : ''}`}
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleBarcodeScan(e.target.value); }}
                onFocus={() => setIsBarcodeFocused(true)}
                onBlur={() => setIsBarcodeFocused(false)}
                placeholder="Click to focus, then scan barcode"
                aria-label="Barcode input - focus, then scan barcode"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          )}

          <div className="warehouse-filters">
            {!allReturned && (
              <button
                type="button"
                className="warehouse-filter-toggle"
                onClick={() => setFiltersExpanded(prev => !prev)}
                aria-expanded={filtersExpanded}
              >
                <span><span aria-hidden="true">{filtersExpanded ? '▾' : '▸'}</span> Filters & Sort</span>
                {!filtersExpanded && (
                  <span className="warehouse-filter-summary">
                    {[
                      selectedTote && `TOTE-${selectedTote.row}-${selectedTote.column}`,
                      bpnFilter && `BPN: ${bpnFilter}`,
                      sortDirection && (sortDirection === 'asc' ? 'BPN ↑' : 'BPN ↓'),
                      hideReturned && 'Returned hidden',
                    ].filter(Boolean).join('  ·  ')}
                  </span>
                )}
              </button>
            )}
            {(allReturned || filtersExpanded) && (
              <>
                {!allReturned && (
                  <>
                    {!bpnFilter && (
                      <>
                        <div className="warehouse-filter-label">
                          {selectedTote
                            ? `Showing TOTE-${selectedTote.row}-${selectedTote.column} — click again to clear`
                            : 'Click a tote to filter items'}
                        </div>
                        <BatchGrid
                          totalRows={selectedBatch.totalRows}
                          totalColumns={selectedBatch.totalColumns}
                          toteCount={selectedBatch.toteCount}
                          toteSize={52}
                          gap={8}
                          highlightedTotes={
                            selectedTote
                              ? [{ row: selectedTote.row, column: selectedTote.column, color: SELECTED_TOTE_COLOR }]
                              : returnToteHighlights
                          }
                          onTotePress={handleToteFilterPress}
                          pressedTotes={selectedTote ? [selectedTote] : []}
                          ariaLabel="Filter items by tote"
                        />
                      </>
                    )}
                    <div className="warehouse-sort-row" role="group" aria-label="Sort by BPN">
                      <span className="warehouse-info-label">Sort by BPN:</span>
                      <button
                        type="button"
                        className={`warehouse-sort-chip${sortDirection === 'asc' ? ' warehouse-sort-chip-active' : ''}`}
                        onClick={() => setSortDirection(prev => (prev === 'asc' ? null : 'asc'))}
                        aria-pressed={sortDirection === 'asc'}
                      >
                        Ascending
                      </button>
                      <button
                        type="button"
                        className={`warehouse-sort-chip${sortDirection === 'desc' ? ' warehouse-sort-chip-active' : ''}`}
                        onClick={() => setSortDirection(prev => (prev === 'desc' ? null : 'desc'))}
                        aria-pressed={sortDirection === 'desc'}
                      >
                        Descending
                      </button>
                    </div>
                  </>
                )}
                <div className="warehouse-switch-row">
                  <span className="warehouse-info-label">Hide returned items</span>
                  <Checkbox checked={hideReturned} onChange={setHideReturned} label="Hide returned items" labelHidden />
                </div>
              </>
            )}
          </div>

          {!allReturned && bpnFilter && (
            <div className="warehouse-bpn-filter-banner">
              <span>Filtering by BPN: {bpnFilter}</span>
              <button type="button" onClick={() => setBpnFilter(null)} aria-label="Clear BPN filter">Clear</button>
            </div>
          )}

          {allReturned && (
            <div className="warehouse-complete">
              <div className="warehouse-complete-title">✓ All items returned</div>
              <Text as="p">
                All {totalItems} items in this batch have been returned to stock.
              </Text>
              <Button variant="primary" onClick={handleMarkBatchReturned}>
                Mark Batch Returned
              </Button>
            </div>
          )}

          <div className="warehouse-list">
            {visibleItems.map(renderItem)}
          </div>
        </>
      ) : batchId && (isLoadingBatch || (isFetchingBatch && !selectedBatch)) ? (
        <div className="warehouse-loading"><LoadingSpinner statusText="Loading batch..." /></div>
      ) : batchId && (isBatchError || !selectedBatch) ? (
        <div className="warehouse-empty">
          <h2>{isAuthError(batchError) ? 'Session expired' : 'Couldn’t load batch'}</h2>
          <Text as="p">
            {isAuthError(batchError)
              ? SESSION_EXPIRED_MESSAGE
              : 'The batch could not be loaded from the server. It may have been completed or claimed by another user.'}
          </Text>
        </div>
      ) : (
        <div className="warehouse-empty">
          <h2>No Active Returns</h2>
          <Text as="p">Start returning from the Returns queue</Text>
        </div>
      )}

      <Modal
        open={popupVisible}
        onClose={handlePopupDismiss}
      >
        <Modal.Section>
        {popupType === 'new-item' && currentItem && (
          <>
            <h2>✓ Item Found</h2>
            <Text as="p" fontWeight="bold">{currentItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{currentItem.quantity}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">BPN:</span>
              <span>{currentItem.bpn}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Tote:</span>
              {renderToteGrid(currentItem.row, currentItem.column, 18)}
            </div>
            {skuToteTotal > 1 && (
              <Text as="p">
                {skuToteTotal - itemQueue.length + 1} of {skuToteTotal} totes with this item
              </Text>
            )}
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handleMarkAsReturned}>Mark as Returned</Button>
            </div>
          </>
        )}

        {popupType === 'already-returned' && currentItem && (
          <>
            <h2>✓ Already Returned</h2>
            <Text as="p" fontWeight="bold">{currentItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{currentItem.quantity}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">BPN:</span>
              <span>{currentItem.bpn}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Tote:</span>
              {renderToteGrid(currentItem.row, currentItem.column, 18)}
            </div>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handleConfirmReturn}>Confirm</Button>
            </div>
          </>
        )}

        {popupType === 'manual-return' && currentItem && (
          <>
            <h2>Confirm Return</h2>
            <Text as="p" fontWeight="bold">{currentItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{currentItem.quantity}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">BPN:</span>
              <span>{currentItem.bpn}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Tote:</span>
              {renderToteGrid(currentItem.row, currentItem.column, 18)}
            </div>
            <div className="warehouse-popup-buttons">
              <Button onClick={handlePopupDismiss}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirmManualReturn}>Confirm Returned</Button>
            </div>
          </>
        )}

        {popupType === 'leave-confirm' && (
          <>
            <h2>Leave This Batch?</h2>
            <Text as="p">
              Parking saves your progress so any user can resume this batch. Stopping discards your progress and returns it to the returns queue.
            </Text>
            {stopError ? (
              <Banner tone="critical"><p>{stopError}</p></Banner>
            ) : null}
            {isStopping ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Releasing batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button variant="primary" onClick={handlePopupDismiss}>Keep Returning</Button>
                <Button onClick={handleParkBatch}>Park Batch</Button>
                <Button variant="primary" tone="critical" onClick={handleStopReturning}>Stop Returning</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'complete-return' && (
          <>
            <h2>Complete Return?</h2>
            <Text as="p">
              Confirm that every leftover item has been returned to stock and the cart is empty.
            </Text>
            {completeError ? (
              <Banner tone="critical"><p>{completeError}</p></Banner>
            ) : null}
            {isMarkingReturned ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Marking batch returned..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={handlePopupDismiss}>Cancel</Button>
                <Button variant="primary" onClick={handleCompleteReturnConfirmed}>Complete</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'park-return' && (
          <>
            <h2>Park Batch?</h2>
            <Text as="p">
              Parking saves your progress and releases this batch so any user can resume it later.
            </Text>
            {parkError ? (
              <Banner tone="critical"><p>{parkError}</p></Banner>
            ) : null}
            {isParking ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Parking batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={handlePopupDismiss}>Cancel</Button>
                <Button variant="primary" onClick={handleParkReturnConfirmed}>Park Batch</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'no-match' && (
          <>
            <h2>No Match Found</h2>
            <Text as="p">
              No matching SKU or UPC was found, and no item BPNs match the scanned value.
            </Text>
            <Text as="p">
              If you were scanning an area, you can move on to the next area.
            </Text>
            <Text as="p">
              If you were scanning a product, the item should not be returned.
            </Text>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handlePopupDismiss}>OK</Button>
            </div>
          </>
        )}
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default ReturnBatch;
